"use client"

import { useEffect } from "react"
import { captureChatError } from "@/lib/sentry-chat"

/**
 * Which email ask the widget put on screen. Mirrors glue's `EMAIL_ASK_KINDS`
 * (`glue/src/email-ask-route.ts`) — a **cross-repo contract**: glue rejects a
 * kind it doesn't know, because the note wording is written per kind.
 */
export type EmailAskKind = "slow_reply" | "email_collect" | "out_of_hours"

interface UseAnnounceEmailAskArgs {
  kind: EmailAskKind
  /** Whether this ask is currently on screen. */
  shown: boolean
  /** Per-contact widget JWT (`session.source_id`) — glue authorizes with it. */
  authToken: string | null
  conversationId: number | null
  /**
   * Epoch ms of the handoff, for the asks that fire because of a wait. Read
   * inside the effect so the elapsed time is measured when the ask actually
   * appears — passing `Date.now() - handoffAt` from render would recompute on
   * every keystroke.
   */
  handoffAt?: number | null
  /** Kinds already reported for this conversation. */
  announced: EmailAskKind[]
  /** Called before the request goes out — see the double-fire note below. */
  onAnnounced: (kind: EmailAskKind) => void
}

/**
 * Tell the backend that we ASKED the visitor for an email, so the Chatwoot
 * timeline records the ask and not only the answer.
 *
 * Every email ask is a client-rendered form, so before this existed an agent saw
 * a bot reply, then silence, then — at best — an address appearing on the
 * contact record with nothing explaining where it came from. A visitor who
 * declined left no trace at all. glue turns this into a private note (agent-only
 * by design: the visitor is already looking at the form, and an outgoing message
 * would be polled back by the transcript catch-up and rendered as a second ask).
 *
 * Fire-and-forget. A failure is reported to Sentry and otherwise ignored: the
 * ask must never depend on Chatwoot being reachable.
 */
export function useAnnounceEmailAsk({
  kind,
  shown,
  authToken,
  conversationId,
  handoffAt,
  announced,
  onAnnounced,
}: UseAnnounceEmailAskArgs): void {
  const alreadyAnnounced = announced.includes(kind)

  useEffect(() => {
    if (!shown || alreadyAnnounced) return
    if (!authToken || conversationId === null) return

    // Marked BEFORE the request, not after. The mark is persisted in the chat
    // session, so a re-render (or a remount from reopening the panel) while the
    // request is in flight would otherwise fire a second one and leave the agent
    // with duplicate notes. The cost is that a failed report is not retried,
    // which is the right trade for a courtesy note.
    onAnnounced(kind)

    const waitedMs = handoffAt ? Date.now() - handoffAt : undefined

    void fetch("/api/chat/email-ask", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Auth-Token": authToken,
      },
      body: JSON.stringify({
        kind,
        conversation_id: conversationId,
        waited_ms: waitedMs,
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`email-ask failed (${res.status})`)
      })
      .catch((err) => {
        captureChatError(err, {
          conversation_id: conversationId,
          extra: { form: "email-ask", kind },
        })
      })
  }, [
    shown,
    alreadyAnnounced,
    authToken,
    conversationId,
    handoffAt,
    kind,
    onAnnounced,
  ])
}
